import React, { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { cachedFetch, cacheKeys } from '@/lib/cache'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { Database, BookOpen, FileText, Mail, CheckCircle, AlertCircle, Clock, ExternalLink, X, RefreshCw } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'

interface ImportSource {
  uid: string
  source_type: string
  source_url: string | null
  status: 'pending' | 'processing' | 'processed' | 'ignored' | 'archived'
  created_at: string
  updated_at: string
  imported_at: string | null
  last_processed: string | null
  raw_content?: string | null // Loaded on demand
  extraction_metadata?: any // Loaded on demand
  relevance_score?: number | null
  data_quality_score?: number | null
}

export function ImportSourcesAdmin() {
  const [sources, setSources] = useState<ImportSource[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 })
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const [selectedSource, setSelectedSource] = useState<ImportSource | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [loadingContent, setLoadingContent] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    fetchSources()
  }, [])

  const fetchSources = async (forceRefresh = false) => {
    try {
      setLoading(true)
      setLoadingProgress({ current: 0, total: 0 })

      const cacheKey = 'import_sources_admin_cache'
      const cacheTimestamp = 'import_sources_cache_timestamp'
      const cacheValidDuration = 24 * 60 * 60 * 1000 // 24 hours

      // Check if we have valid cached data
      if (!forceRefresh) {
        const cachedData = localStorage.getItem(cacheKey)
        const cachedTimestamp = localStorage.getItem(cacheTimestamp)

        if (cachedData && cachedTimestamp) {
          const timestamp = parseInt(cachedTimestamp)
          const isValid = Date.now() - timestamp < cacheValidDuration

          if (isValid) {
            console.log('Using cached import sources data')
            const parsedData = JSON.parse(cachedData)
            setSources(parsedData)
            setLoading(false)
            return
          } else {
            console.log('Cache expired, refreshing data')
          }
        }
      } else {
        console.log('Force refresh requested')
      }

      console.log('Loading fresh import sources data...')

      // Load optimized data (without heavy raw_content field)
      const { data, error } = await supabase
        .from('import_sources')
        .select(`
          uid,
          source_type,
          source_url,
          status,
          created_at,
          updated_at,
          imported_at,
          last_processed,
          relevance_score,
          data_quality_score
        `)
        .order('created_at', { ascending: false })
        .limit(1000) // Load more but without heavy fields

      if (error) {
        console.error('Import sources error:', error)
        throw error
      }

      const sources = data || []
      console.log(`Loaded ${sources.length} import sources`)

      // Cache the data
      localStorage.setItem(cacheKey, JSON.stringify(sources))
      localStorage.setItem(cacheTimestamp, Date.now().toString())

      setSources(sources)
    } catch (error) {
      console.error('Error fetching sources:', error)
      toast({
        title: 'Error loading sources',
        description: 'Could not fetch import sources',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const getSourceStatus = (status: string) => {
    switch (status) {
      case 'processed':
        return { status: 'processed', label: 'Processed', color: 'text-green-600 bg-green-50', icon: CheckCircle }
      case 'processing':
        return { status: 'processing', label: 'Processing', color: 'text-blue-600 bg-blue-50', icon: Clock }
      case 'pending':
        return { status: 'pending', label: 'Pending', color: 'text-yellow-600 bg-yellow-50', icon: Clock }
      case 'ignored':
        return { status: 'ignored', label: 'Ignored', color: 'text-gray-600 bg-gray-50', icon: AlertCircle }
      case 'archived':
        return { status: 'archived', label: 'Archived', color: 'text-purple-600 bg-purple-50', icon: AlertCircle }
      default:
        return { status: 'unknown', label: 'Unknown', color: 'text-gray-600 bg-gray-50', icon: AlertCircle }
    }
  }

  const getSourceIcon = (type: string) => {
    switch (type) {
      case 'neoom': return <BookOpen className="h-4 w-4" />
      case 'notion': return <FileText className="h-4 w-4" />
      case 'email': return <Mail className="h-4 w-4" />
      default: return <Database className="h-4 w-4" />
    }
  }

  const getSourceName = (type: string) => {
    switch (type) {
      case 'neoom': return 'Neoom Wiki'
      case 'notion': return 'Notion Wiki'
      case 'email': return 'Email'
      default: return type
    }
  }

  // Count sources by type
  const sourceCounts = sources.reduce((acc, source) => {
    acc[source.source_type] = (acc[source.source_type] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  // Filter sources based on selected filter
  const filteredSources = selectedFilter
    ? sources.filter(source => source.source_type === selectedFilter)
    : sources

  const loadSourceDetails = async (source: ImportSource) => {
    try {
      setLoadingContent(true)

      // Check if content already loaded
      if (source.raw_content !== undefined) {
        return source
      }

      console.log('Loading raw content for source:', source.uid)

      const { data, error } = await supabase
        .from('import_sources')
        .select('raw_content, extraction_metadata')
        .eq('uid', source.uid)
        .single()

      if (error) throw error

      // Update the source in the list with loaded content
      const updatedSource = { ...source, ...data }
      setSources(prev => prev.map(s => s.uid === source.uid ? updatedSource : s))

      return updatedSource
    } catch (error) {
      console.error('Error loading source details:', error)
      toast({
        title: 'Fehler beim Laden',
        description: 'Content konnte nicht geladen werden',
        variant: 'destructive'
      })
      return source
    } finally {
      setLoadingContent(false)
    }
  }

  const handleSourceClick = async (source: ImportSource) => {
    const sourceWithContent = await loadSourceDetails(source)
    setSelectedSource(sourceWithContent)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedSource(null)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-light tracking-tight">Import Sources</h1>
          <p className="text-muted-foreground">View and monitor all import source records</p>
          {loading && (
            <div className="mt-3 p-3 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-muted-foreground/30 border-t-primary"></div>
                  <div>
                    <div className="font-medium text-sm">
                      Lade Import Sources...
                    </div>
                    <div className="text-muted-foreground text-xs">
                      Daten werden aus LocalStorage geladen oder von DB abgerufen
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => fetchSources(true)}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Force Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {['neoom', 'notion', 'email'].map(type => (
          <Card
            key={type}
            className={`cursor-pointer transition-colors ${
              selectedFilter === type
                ? 'bg-primary text-primary-foreground border-primary'
                : 'hover:bg-muted/50'
            }`}
            onClick={() => setSelectedFilter(selectedFilter === type ? null : type)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {getSourceName(type)}
              </CardTitle>
              {getSourceIcon(type)}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {sourceCounts[type] || 0}
              </div>
              <p className={`text-xs ${
                selectedFilter === type ? 'text-primary-foreground/70' : 'text-muted-foreground'
              }`}>
                Import sources
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sources List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedFilter ? `${getSourceName(selectedFilter)} Import Sources` : 'All Import Sources'}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {selectedFilter
              ? `Showing ${filteredSources.length} records from ${getSourceName(selectedFilter)}`
              : `Historical records of imported content sources (${filteredSources.length} total)`
            }
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : filteredSources.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              {selectedFilter ? `No ${getSourceName(selectedFilter)} import sources found.` : 'No import sources found.'}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSources.map(source => {
                const statusInfo = getSourceStatus(source.status)
                const StatusIcon = statusInfo.icon

                return (
                  <div
                    key={source.uid}
                    className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => handleSourceClick(source)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {getSourceIcon(source.source_type)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{source.uid}</p>
                            <Badge variant="outline" className={statusInfo.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusInfo.label}
                            </Badge>
                            {loadingContent && selectedSource?.uid === source.uid && (
                              <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-3 w-3 border border-muted border-t-primary"></div>
                                <span className="text-xs text-muted-foreground">Lade Content...</span>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Type: {getSourceName(source.source_type)}
                          </p>
                          {source.source_url && (
                            <div className="flex items-center gap-1 mt-1">
                              <ExternalLink className="h-3 w-3 text-muted-foreground" />
                              <a
                                href={source.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {source.uid}
                              </a>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Created: {new Date(source.created_at).toLocaleDateString('de-DE', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw Content Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="text-lg font-semibold">
              Raw Content: {selectedSource?.uid}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {getSourceName(selectedSource?.source_type || '')}
            </p>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6">
            <ScrollArea className="h-[calc(95vh-120px)] w-full">
              <div className="bg-muted rounded-lg p-4">
                <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                  {selectedSource?.raw_content || 'No raw content available'}
                </pre>
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}